import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import DateInput from '../ui/date-input';
import { Label } from '../ui/label';
import { useToast } from '../../hooks/use-toast';
import { receptionApiErrorMessage } from './receptionConstants';
import { handleBlobDownloadResponse } from '../../utils/blobDownload';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const REPORTS = [
  { key: 'inquiry-source', label: 'Inquiry Source Analysis' },
  { key: 'monthly-trends', label: 'Monthly Inquiry Trends' },
  { key: 'conversion-rate', label: 'Conversion Rate' },
  { key: 'counselor-performance', label: 'Counselor Performance' },
  { key: 'followup-status', label: 'Follow-up Status' },
];

const COLORS = ['#6366f1', '#8b5cf6', '#a855f7', '#d946ef', '#ec4899', '#f43f5e', '#f97316', '#eab308'];

function reportLoadErrorMessage(error) {
  if (error?.code === 'ERR_NETWORK' || error?.message === 'Network Error') {
    return 'Could not reach the API server. Check that the backend is running and review server logs for this report.';
  }
  return receptionApiErrorMessage(error);
}

function buildChartData(active, items) {
  if (!items?.length) return [];
  if (active === 'counselor-performance') {
    return items.map((row) => ({
      name: row.counselor_name || `${row.first_name || ''} ${row.last_name || ''}`.trim() || 'Unknown',
      count: Number(row.total_inquiries) || 0,
      conversions: Number(row.conversions) || 0,
    }));
  }
  return items.map((row) => ({
    name: row.inquiry_source?.replace(/_/g, ' ') || row.month || `${row.first_name || ''} ${row.last_name || ''}`.trim() || 'Unknown',
    count: row.count || row.total_inquiries || 0,
    conversions: row.conversions || 0,
  }));
}

export default function ReceptionReports() {
  const { toast } = useToast();
  const [active, setActive] = useState('inquiry-source');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { format: 'json' };
      if (from) params.from = from;
      if (to) params.to = to;
      const res = await api.get(`/reception/reports/${active}`, { params });
      setData(res.data?.data || null);
    } catch (e) {
      toast({ title: 'Error', description: reportLoadErrorMessage(e), variant: 'destructive' });
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [active]);

  const exportReport = async (format) => {
    try {
      const params = { format };
      if (from) params.from = from;
      if (to) params.to = to;
      const res = await api.get(`/reception/reports/${active}`, { params, responseType: 'blob' });
      await handleBlobDownloadResponse(res, format, `reception-${active}.${format}`);
    } catch (e) {
      toast({ title: 'Export failed', description: receptionApiErrorMessage(e, 'Export failed'), variant: 'destructive' });
    }
  };

  const chartData = buildChartData(active, data?.items);
  const showChart = ['inquiry-source', 'monthly-trends', 'counselor-performance'].includes(active);
  const chartEmpty = !loading && showChart && data && chartData.length === 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Reception Reports</h1>
        <p className="text-muted-foreground">Analytics and export for admission inquiries</p>
      </div>
      <Card>
        <CardContent className="p-4 flex flex-wrap gap-3 items-end">
          <div><Label>From</Label><DateInput value={from} onChange={setFrom} /></div>
          <div><Label>To</Label><DateInput value={to} onChange={setTo} /></div>
          <Button onClick={load}>Apply</Button>
          <Button variant="outline" onClick={() => exportReport('pdf')}>Export PDF</Button>
          <Button variant="outline" onClick={() => exportReport('xlsx')}>Export Excel</Button>
        </CardContent>
      </Card>
      <div className="flex flex-wrap gap-2">
        {REPORTS.map((r) => (
          <Button key={r.key} variant={active === r.key ? 'default' : 'outline'} onClick={() => setActive(r.key)}>{r.label}</Button>
        ))}
      </div>
      {loading ? <p className="text-muted-foreground">Loading…</p> : (
        <>
          {active === 'conversion-rate' && data && (
            <Card><CardHeader><CardTitle>Conversion Rate</CardTitle></CardHeader>
              <CardContent className="text-lg">
                <p>Total inquiries: <strong>{data.total}</strong></p>
                <p>Converted: <strong>{data.converted}</strong></p>
                <p>Rate: <strong>{data.rate_percent}%</strong></p>
              </CardContent>
            </Card>
          )}
          {active === 'followup-status' && data && (
            <Card><CardHeader><CardTitle>Follow-up Status</CardTitle></CardHeader>
              <CardContent className="space-y-1">
                <p>Pending: <strong>{data.pending}</strong></p>
                <p>Missed: <strong>{data.missed}</strong></p>
                <p>Completed follow-up entries: <strong>{data.completed_followups}</strong></p>
              </CardContent>
            </Card>
          )}
          {chartEmpty && (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                {active === 'counselor-performance'
                  ? 'No counselor data for the selected dates. Assign inquiries to counselors to see performance here.'
                  : 'No data for the selected dates.'}
              </CardContent>
            </Card>
          )}
          {chartData.length > 0 && (
            <Card>
              <CardHeader><CardTitle>{REPORTS.find((r) => r.key === active)?.label}</CardTitle></CardHeader>
              <CardContent className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  {active === 'inquiry-source' ? (
                    <PieChart>
                      <Pie data={chartData} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                        {chartData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  ) : (
                    <BarChart data={chartData}>
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#6366f1" />
                      {active === 'counselor-performance' && <Bar dataKey="conversions" fill="#22c55e" />}
                    </BarChart>
                  )}
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
